/**
 * Cartão de indicador dos relatórios (Total, Vendido, Ticket Médio...).
 *
 * Mora em arquivo próprio porque não é mais exclusivo das estatísticas de uma seção:
 * a produtividade soma os tipos selecionados e publica os próprios totais no mesmo
 * padrão. Dois desenhos diferentes para o mesmo tipo de número na mesma tela fazem o
 * leitor achar que são coisas de naturezas distintas.
 */

export type KpiAccent =
    | 'primary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | 'neutral'

export interface KpiCardProps {
    title: string
    value: string | number
    icon: React.ReactNode
    accent: KpiAccent
    hint?: string
}

const ACCENTS: Record<KpiAccent, { bg: string; text: string; ring: string }> = {
    primary: { bg: 'bg-secondary/10', text: 'text-secondary', ring: 'ring-secondary/20' },
    success: { bg: 'bg-success/10', text: 'text-success', ring: 'ring-success/20' },
    error: { bg: 'bg-error/10', text: 'text-error', ring: 'ring-error/20' },
    info: { bg: 'bg-info/10', text: 'text-info', ring: 'ring-info/20' },
    warning: { bg: 'bg-warning/10', text: 'text-warning', ring: 'ring-warning/20' },
    neutral: { bg: 'bg-base-200', text: 'text-base-content', ring: 'ring-base-300' },
}

export function KpiCard({ title, value, icon, accent, hint }: KpiCardProps) {
    const a = ACCENTS[accent]
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex items-start gap-3 shadow-sm">
            <div
                className={`w-11 h-11 rounded-lg ${a.bg} ${a.text} flex items-center justify-center text-2xl shrink-0`}
            >
                {icon}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-xs uppercase tracking-wide text-base-content/50 font-medium">
                    {title}
                </span>
                <span className={`text-xl font-bold ${a.text} truncate`}>{value}</span>
                {hint && (
                    <span className="text-xs text-base-content/50 mt-0.5">{hint}</span>
                )}
            </div>
        </div>
    )
}

export function KpiSkeleton() {
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex items-start gap-3">
            <div className="skeleton w-11 h-11 rounded-lg shrink-0" />
            <div className="flex flex-col gap-2 w-full">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton h-6 w-24" />
            </div>
        </div>
    )
}

export default KpiCard
